import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canSalesWrite, useAuth } from "@/lib/auth";
import { SALES } from "@/constants/testIds";

const SELLER_STATE = "KA";
const IN_STATES = [
  "AP","AR","AS","BR","CG","GA","GJ","HR","HP","JH","KA","KL","MP","MH",
  "MN","ML","MZ","NL","OD","PB","RJ","SK","TN","TS","TR","UP","UK","WB",
  "AN","CH","DN","DL","JK","LA","LD","PY",
];

const toDec = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// GST calculation per line (mirrors backend `_compute_line`).
function computeLine(l, isIgst) {
  const qty = 1;
  const unit = toDec(l.unit_price, 0);
  const disc = toDec(l.discount_amount, 0);
  const pct = toDec(l.gst_rate_pct, 18);
  const gross = unit * qty;
  const taxable = Math.max(0, gross - disc);
  let cgst = 0, sgst = 0, igst = 0;
  if (isIgst) {
    igst = (taxable * pct) / 100;
  } else {
    cgst = (taxable * pct) / 200;
    sgst = (taxable * pct) / 200;
  }
  const total = taxable + cgst + sgst + igst;
  return { gross, taxable, cgst, sgst, igst, total };
}

function Field({ label, testId, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
      {typeof children === "function"
        ? children({ testId, cls: "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" })
        : children}
    </label>
  );
}

function TxtField({ label, testId, value, onChange, placeholder, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
      <input
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
        {...rest}
      />
    </label>
  );
}

export default function NewSalesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const allowed = canSalesWrite(user);

  useEffect(() => {
    if (!allowed) navigate("/admin/sales", { replace: true });
  }, [allowed, navigate]);

  const [cust, setCust] = useState({
    customer_type: "WALK_IN",
    full_name: "",
    mobile: "",
    email: "",
    company_name: "",
    gstin: "",
  });
  // V2.1 — External Order No. is required when the channel is Amazon or
  // RentOPC.com. Invoice No. is optional across all three channels.
  const [externalOrderNo, setExternalOrderNo] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [bill, setBill] = useState({
    address_line1: "", address_line2: "", city: "", state: "KA", pincode: "",
  });
  const [shipSame, setShipSame] = useState(true);
  const [ship, setShip] = useState({
    name: "", mobile: "", address_line1: "", city: "", state: "KA", pincode: "",
  });
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  // V2.0 — Editable "Invoice Date" (business/accounting date shown on the
  // invoice). Defaults to today; sent to backend as `invoice_date`, which
  // maps to the existing sales_order.order_date column. The system
  // "Created Date" (created_at) is set automatically and is not exposed.
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });

  // Passport search
  const [searchQ, setSearchQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isIgst = useMemo(() => {
    const st = (bill.state || "").trim().toUpperCase();
    return st !== "" && st !== SELLER_STATE;
  }, [bill.state]);

  const totals = useMemo(() => {
    let subtotal = 0, discount = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0, grand = 0;
    lines.forEach((l) => {
      const c = computeLine(l, isIgst);
      subtotal += c.gross;
      discount += toDec(l.discount_amount, 0);
      taxable += c.taxable;
      cgst += c.cgst;
      sgst += c.sgst;
      igst += c.igst;
      grand += c.total;
    });
    return { subtotal, discount, taxable, cgst, sgst, igst, grand };
  }, [lines, isIgst]);

  const searchPassports = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    setSearchError(null);
    setSearchResults([]);
    try {
      const { data } = await api.get("/inventory", {
        params: { q: searchQ.trim(), status: "AVAILABLE", page: 1, page_size: 25 },
      });
      setSearchResults(data.items);
    } catch (e) {
      setSearchError(errorToMessage(e, "Search failed"));
    } finally {
      setSearching(false);
    }
  };

  const addLine = (p) => {
    if (lines.some((l) => l.passport_id === p.passport_id)) return;
    setLines((prev) => [...prev, {
      passport_id: p.passport_id,
      passport_number: p.passport_number,
      serial_number: p.serial_number,
      brand: p.brand,
      model: p.model,
      description: `${p.brand || ""} ${p.model || ""}`.trim(),
      unit_price: "",
      discount_amount: "0",
      gst_rate_pct: "18",
    }]);
    setSearchResults((prev) => prev.filter((r) => r.passport_id !== p.passport_id));
  };

  const updLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };
  const rmLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const requiresOrderNo = cust.customer_type === "AMAZON" || cust.customer_type === "RENTOPC";
  const mobileRequired = cust.customer_type !== "AMAZON";
  const canSubmit = cust.full_name.trim() && (!mobileRequired || cust.mobile.trim())
    && bill.state && lines.length > 0
    && lines.every((l) => toDec(l.unit_price, -1) > 0)
    && (!requiresOrderNo || externalOrderNo.trim().length > 0);

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        customer: {
          customer_type: cust.customer_type,
          full_name: cust.full_name.trim(),
          mobile: cust.mobile.trim() || null,
          email: cust.email.trim() || null,
          company_name: cust.company_name.trim() || null,
          gstin: cust.gstin.trim() || null,
        },
        billing: {
          name: cust.full_name.trim(),
          mobile: cust.mobile.trim() || null,
          address_line1: bill.address_line1 || null,
          address_line2: bill.address_line2 || null,
          city: bill.city || null,
          state: bill.state || null,
          pincode: bill.pincode || null,
        },
        shipping: shipSame ? null : {
          name: ship.name || cust.full_name.trim(),
          mobile: ship.mobile || cust.mobile.trim() || null,
          address_line1: ship.address_line1 || null,
          city: ship.city || null,
          state: ship.state || null,
          pincode: ship.pincode || null,
        },
        lines: lines.map((l) => ({
          passport_id: l.passport_id,
          description: l.description || null,
          unit_price: String(l.unit_price),
          discount_amount: String(l.discount_amount || "0"),
          gst_rate_pct: String(l.gst_rate_pct || "18"),
        })),
        remarks: remarks.trim() || null,
        invoice_date: invoiceDate || null,
        external_order_no: requiresOrderNo ? externalOrderNo.trim() : null,
        invoice_no: invoiceNo.trim() || null,
      };
      const { data } = await api.post("/sales", payload);
      navigate(`/admin/sales/${data.id}`);
    } catch (e) {
      setSubmitError(errorToMessage(e, "Could not create sales order"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="new-sales-page">
      <div className="flex items-baseline justify-between">
        <div>
          <button onClick={() => navigate("/admin/sales")}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-2">
            <ArrowLeft size={14} /> Sales
          </button>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">New Sales Order</h1>
          <p className="text-sm text-slate-500 mt-1">
            Draft a new order. Confirming reserves inventory and generates a pack task.
          </p>
        </div>
      </div>

      {/* Customer */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Customer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Type">
            <select data-testid={SALES.newCustType} value={cust.customer_type}
              onChange={(e) => {
                const v = e.target.value;
                setCust({ ...cust, customer_type: v });
                if (v === "WALK_IN") setExternalOrderNo("");
              }}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="WALK_IN">Walk-in</option>
              <option value="AMAZON">Amazon</option>
              <option value="RENTOPC">RentOPC.com</option>
            </select>
          </Field>
          <TxtField label="Full name *" testId={SALES.newCustName}
            value={cust.full_name} onChange={(e) => setCust({ ...cust, full_name: e.target.value })}
            placeholder="Customer full name" />
          <TxtField label={mobileRequired ? "Mobile *" : "Mobile (optional)"} testId={SALES.newCustMobile}
            value={cust.mobile} onChange={(e) => setCust({ ...cust, mobile: e.target.value })}
            placeholder={mobileRequired ? "10-digit mobile" : "Optional for Amazon"} />
          <TxtField label="Email" testId={SALES.newCustEmail}
            value={cust.email} onChange={(e) => setCust({ ...cust, email: e.target.value })}
            placeholder="optional" />
          <TxtField label="Company (for GST invoice)" testId={SALES.newCustCompany}
            value={cust.company_name} onChange={(e) => setCust({ ...cust, company_name: e.target.value })} />
          <TxtField label="GSTIN (15 chars)" testId={SALES.newCustGstin}
            value={cust.gstin} onChange={(e) => setCust({ ...cust, gstin: e.target.value.toUpperCase() })} />
          {requiresOrderNo && (
            <TxtField
              label={`Order No. * (${cust.customer_type === "AMAZON" ? "Amazon" : "RentOPC.com"})`}
              testId="new-sales-external-order-no"
              value={externalOrderNo}
              onChange={(e) => setExternalOrderNo(e.target.value)}
              placeholder={cust.customer_type === "AMAZON" ? "e.g. 405-1234567-1234567" : "e.g. RO-2026-000123"}
            />
          )}
          <TxtField
            label="Invoice No."
            testId="new-sales-invoice-no"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            placeholder="Optional — leave blank to auto-generate later"
          />
        </div>
      </section>

      {/* Billing */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Billing address</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <TxtField label="Address line 1" testId={SALES.newBillAddr1}
              value={bill.address_line1}
              onChange={(e) => setBill({ ...bill, address_line1: e.target.value })} />
          </div>
          <TxtField label="Address line 2" testId={SALES.newBillAddr2}
            value={bill.address_line2}
            onChange={(e) => setBill({ ...bill, address_line2: e.target.value })} />
          <TxtField label="City" testId={SALES.newBillCity}
            value={bill.city} onChange={(e) => setBill({ ...bill, city: e.target.value })} />
          <Field label="State *">
            <select data-testid={SALES.newBillState} value={bill.state}
              onChange={(e) => setBill({ ...bill, state: e.target.value })}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
              {IN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <TxtField label="Pincode" testId={SALES.newBillPincode}
            value={bill.pincode} onChange={(e) => setBill({ ...bill, pincode: e.target.value })} />
        </div>
        <div className="text-xs text-slate-500">
          Seller state <span className="font-mono">{SELLER_STATE}</span> · GST split will be
          <span className="font-semibold ml-1">{isIgst ? "IGST (inter-state)" : "CGST + SGST (intra-state)"}</span>.
        </div>
      </section>

      {/* Shipping */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Shipping address</h2>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" data-testid={SALES.newShipSameAsBill}
              checked={shipSame} onChange={(e) => setShipSame(e.target.checked)} />
            Same as billing
          </label>
        </div>
        {!shipSame && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TxtField label="Name" testId={SALES.newShipName}
              value={ship.name} onChange={(e) => setShip({ ...ship, name: e.target.value })} />
            <TxtField label="Mobile" testId={SALES.newShipMobile}
              value={ship.mobile} onChange={(e) => setShip({ ...ship, mobile: e.target.value })} />
            <TxtField label="Address" testId={SALES.newShipAddr1}
              value={ship.address_line1} onChange={(e) => setShip({ ...ship, address_line1: e.target.value })} />
            <TxtField label="City" testId={SALES.newShipCity}
              value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
            <Field label="State">
              <select data-testid={SALES.newShipState} value={ship.state}
                onChange={(e) => setShip({ ...ship, state: e.target.value })}
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                {IN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <TxtField label="Pincode" testId={SALES.newShipPincode}
              value={ship.pincode} onChange={(e) => setShip({ ...ship, pincode: e.target.value })} />
          </div>
        )}
      </section>

      {/* Line items */}
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Line items</h2>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <input data-testid={SALES.newLineSearch}
              value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchPassports(); } }}
              placeholder="Search AVAILABLE inventory by passport, barcode, serial, brand or model"
              className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
          </div>
          <button type="button" data-testid={SALES.newLineSearchBtn}
            onClick={searchPassports}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Search size={14} /> Search
          </button>
        </div>

        {searchError && <div className="text-xs text-rose-700">{searchError}</div>}

        {searching && <div className="text-sm text-slate-400">Searching…</div>}
        {searchResults.length > 0 && (
          <div className="rounded-md border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Passport</th>
                  <th className="text-left px-3 py-2">Serial · Brand · Model</th>
                  <th className="text-left px-3 py-2">Location</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {searchResults.map((p) => (
                  <tr key={p.passport_id} data-testid={SALES.newLineResult(p.passport_id)} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">{p.passport_number}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono text-xs text-slate-600">{p.serial_number}</div>
                      <div className="text-slate-900">{p.brand} {p.model}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{p.current_location || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={SALES.newLineAdd(p.passport_id)}
                        onClick={() => addLine(p)}
                        className="inline-flex items-center gap-1 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-800">
                        <Plus size={12} /> Add
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {lines.length > 0 && (
          <div className="rounded-md border border-slate-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Passport · Serial</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2">Unit price *</th>
                  <th className="text-right px-3 py-2">Discount</th>
                  <th className="text-right px-3 py-2">GST %</th>
                  <th className="text-right px-3 py-2">Line total</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const c = computeLine(l, isIgst);
                  return (
                    <tr key={l.passport_id} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs text-slate-900">{l.passport_number}</div>
                        <div className="font-mono text-xs text-slate-500">{l.serial_number}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-800">
                        {l.brand} {l.model}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min={0} step="0.01"
                          data-testid={SALES.newLinePrice(idx)}
                          value={l.unit_price}
                          onChange={(e) => updLine(idx, { unit_price: e.target.value })}
                          className="w-28 rounded-md border border-slate-200 px-2 py-1 text-right text-sm" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min={0} step="0.01"
                          data-testid={SALES.newLineDiscount(idx)}
                          value={l.discount_amount}
                          onChange={(e) => updLine(idx, { discount_amount: e.target.value })}
                          className="w-24 rounded-md border border-slate-200 px-2 py-1 text-right text-sm" />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" min={0} max={28} step="0.01"
                          data-testid={SALES.newLineGst(idx)}
                          value={l.gst_rate_pct}
                          onChange={(e) => updLine(idx, { gst_rate_pct: e.target.value })}
                          className="w-16 rounded-md border border-slate-200 px-2 py-1 text-right text-sm" />
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs text-slate-900">
                        ₹{c.total.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button data-testid={SALES.newLineRemove(idx)}
                          onClick={() => rmLine(idx)}
                          className="text-slate-400 hover:text-rose-600">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div data-testid={SALES.newSummary} className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-slate-500">Subtotal</div><div className="font-mono">₹{totals.subtotal.toFixed(2)}</div></div>
          <div><div className="text-xs text-slate-500">Discount</div><div className="font-mono">₹{totals.discount.toFixed(2)}</div></div>
          <div><div className="text-xs text-slate-500">Taxable</div><div className="font-mono">₹{totals.taxable.toFixed(2)}</div></div>
          {isIgst
            ? <div><div className="text-xs text-slate-500">IGST</div><div className="font-mono">₹{totals.igst.toFixed(2)}</div></div>
            : (
              <div>
                <div className="text-xs text-slate-500">CGST + SGST</div>
                <div className="font-mono">₹{(totals.cgst + totals.sgst).toFixed(2)}</div>
              </div>
            )}
          <div className="col-span-2 sm:col-span-4 border-t border-slate-100 pt-2 flex items-baseline justify-between">
            <span className="text-sm text-slate-500">Grand total</span>
            <span className="font-mono text-lg font-semibold text-slate-900">₹{totals.grand.toFixed(2)}</span>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Invoice Date</h2>
        <div className="flex items-center gap-3">
          <input
            type="date"
            data-testid="new-sales-invoice-date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800"
          />
          <p className="text-xs text-slate-500">
            Shown on the printed invoice. Defaults to today. The system
            Created Date is recorded automatically and is separate.
          </p>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm space-y-4">
        <h2 className="text-sm font-semibold text-slate-900">Remarks</h2>
        <textarea data-testid={SALES.newRemarks}
          value={remarks} onChange={(e) => setRemarks(e.target.value)}
          rows={3} placeholder="Optional internal notes"
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
      </section>

      {submitError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pb-8">
        <button data-testid={SALES.newCancel} onClick={() => navigate("/admin/sales")}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button data-testid={SALES.newSubmit} disabled={!canSubmit || submitting}
          onClick={submit}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
          {submitting ? "Creating…" : "Create draft"}
        </button>
      </div>
    </div>
  );
}
